/**
 * 商品頁圖片輪播：大圖左右切換 + 下方縮圖切換。
 *
 * 大圖是原生的水平 scroll-snap 容器，所以手機上的滑動手勢、鍵盤方向鍵、
 * 捲動定位都由瀏覽器負責；這支腳本只做三件事：
 *   1. 左右箭頭與縮圖 → 捲到指定那一張
 *   2. 大圖被捲動（含手勢滑動）→ 同步縮圖的選中狀態
 *   3. 收到 `product-gallery:show` 事件 → 捲到對應 media（選規格時換圖用）
 *
 * 只有一張圖時 Liquid 不會輸出箭頭與縮圖，這裡也會直接不啟動。
 */
(() => {
  const gallery = document.querySelector('[data-product-gallery]');
  if (!gallery) return;

  const track = gallery.querySelector('[data-gallery-track]');
  const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
  if (!track || slides.length < 2) return;

  const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
  const thumbList = gallery.querySelector('[data-gallery-thumbs]');
  const prevButton = gallery.querySelector('[data-gallery-prev]');
  const nextButton = gallery.querySelector('[data-gallery-next]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let current = 0;
  let syncLocked = false;
  let unlockTimer = 0;

  /**
   * 更新縮圖的選中狀態，並把選中的縮圖捲進縮圖列的可視範圍。
   * 只捲縮圖列本身，不用 scrollIntoView——那會連整個頁面一起捲動。
   * @param {number} index - 目前顯示的圖片索引
   * @returns {void}
   */
  function setCurrent(index) {
    current = index;

    thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    const active = thumbs[index];
    if (thumbList && active) {
      const target = active.offsetLeft - (thumbList.clientWidth - active.offsetWidth) / 2;
      thumbList.scrollTo({ left: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  }

  /**
   * 把大圖捲到指定那一張。索引超出範圍時循環（第一張往前＝最後一張）。
   * @param {number} index - 目標圖片索引
   * @returns {void}
   */
  function goTo(index) {
    const next = (index + slides.length) % slides.length;
    lockScrollSync();
    track.scrollTo({
      left: next * track.clientWidth,
      behavior: reduceMotion.matches ? 'auto' : 'smooth',
    });
    setCurrent(next);
  }

  /**
   * 程式捲動（箭頭、縮圖、換規格）進行中，暫停「依捲動位置回推第幾張」。
   *
   * 不暫停的話，點縮圖從第 1 張跳到第 4 張時，捲動途中會依序經過第 2、3 張，
   * 監聽器每一格都把選中狀態改成當下經過的那張——縮圖高亮會跟著閃過中間幾張，
   * 而不是直接停在被點的那張。
   *
   * 解鎖以 `scrollend` 為準（支援的瀏覽器）；另外一律設一個保險計時器，
   * 因為「已經在目標位置」時不會有捲動、也就不會有 scrollend，鎖不能永遠不放。
   */
  function lockScrollSync() {
    syncLocked = true;
    clearTimeout(unlockTimer);
    unlockTimer = setTimeout(unlockScrollSync, 1200);
  }

  function unlockScrollSync() {
    syncLocked = false;
    clearTimeout(unlockTimer);
    syncFromScroll();
  }

  /**
   * 依大圖目前的捲動位置回推是第幾張，並同步縮圖。
   * @returns {void}
   */
  function syncFromScroll() {
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== current && index >= 0 && index < slides.length) setCurrent(index);
  }

  if (prevButton) prevButton.addEventListener('click', () => goTo(current - 1));
  if (nextButton) nextButton.addEventListener('click', () => goTo(current + 1));
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

  // 大圖被手勢或鍵盤捲動時，依捲動位置回推目前是哪一張
  let scrollFrame = 0;
  track.addEventListener('scroll', () => {
    if (syncLocked || scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      syncFromScroll();
    });
  }, { passive: true });
  track.addEventListener('scrollend', () => {
    if (syncLocked) unlockScrollSync();
  });

  // 視窗尺寸改變後，維持在同一張圖上，不要卡在兩張之間
  window.addEventListener('resize', () => {
    track.scrollTo({ left: current * track.clientWidth, behavior: 'auto' });
  });

  // 鍵盤：大圖取得焦點時用左右鍵切換（順便補上頭尾循環）
  track.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current - 1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current + 1); }
  });

  // 選規格時由商品頁的規格腳本發出，帶該規格主圖的 media id
  document.addEventListener('product-gallery:show', (event) => {
    const mediaId = String(event.detail && event.detail.mediaId);
    const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) goTo(index);
  });

  setCurrent(0);
})();
